import * as React from 'react'
import * as ReactDOM from 'react-dom'
import moment from 'moment'
import { CountryModel } from '@/app/models/timesheet/countryModel'
import useBusinessTripService from '@/modules/drivers/services/BusinessTripService'
import MonthPicker from '@/modules/drivers/shared/date/MonthPicker'
import { DateField } from '@/ui/form/controls/DateField'
import type { DialogState } from '../../hooks/useBusinessTripContextMenuActions'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { translateCountryName } from '@/app/common/countryNames'
import { Dropdown } from '@/ui/form/controls/Dropdown'

interface DialogShellProps {
  title: string
  onClose: () => void
  onConfirm: () => void
  children: React.ReactNode
  confirmDisabled?: boolean
}

const DialogShell: React.FC<DialogShellProps> = ({
  title,
  onClose,
  onConfirm,
  children,
  confirmDisabled,
}) => {
  const { t } = useLocalize()
  return ReactDOM.createPortal(
    <div className="tw-fixed tw-inset-0 tw-z-[9999] tw-flex tw-items-center tw-justify-center">
      <div
        className="tw-absolute tw-inset-0 tw-bg-black/40"
        onClick={onClose}
      />
      <div className="tw-relative tw-w-96 tw-max-w-[calc(100vw-2rem)] tw-rounded-lg tw-bg-white tw-p-5 tw-shadow-xl">
        <h3 className="tw-mb-4 tw-text-sm tw-font-semibold tw-text-gray-800">
          {title}
        </h3>
        <div className="tw-mb-4">{children}</div>
        <div className="tw-flex tw-justify-end tw-gap-2">
          <button
            type="button"
            className="tw-rounded tw-border tw-border-gray-300 tw-px-3 tw-py-1.5 tw-text-xs tw-text-gray-600 hover:tw-bg-gray-50"
            onClick={onClose}
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            disabled={confirmDisabled}
            className="tw-rounded tw-bg-primary tw-px-3 tw-py-1.5 tw-text-xs tw-text-white hover:tw-bg-primary/90 disabled:tw-opacity-50"
            onClick={onConfirm}
          >
            {t('common.confirm')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

interface CountryPickerDialogProps {
  companyId: string
  currentCountry?: string
  onClose: () => void
  onConfirm: (country: CountryModel) => void
}

export const CountryPickerDialog: React.FC<CountryPickerDialogProps> = ({
  companyId,
  currentCountry,
  onClose,
  onConfirm,
}) => {
  const { t, activeLanguage } = useLocalize()
  const { getCountries } = useBusinessTripService()
  const [countries, setCountries] = React.useState<CountryModel[]>([])
  const [selected, setSelected] = React.useState<CountryModel | null>(null)

  React.useEffect(() => {
    getCountries({ companyId }).then((data) => {
      setCountries(data)
      const match = currentCountry
        ? data.find(
            (c) =>
              c.name?.toLowerCase() === currentCountry.toLowerCase() ||
              c.code?.toLowerCase() === currentCountry.toLowerCase(),
          )
        : null
      setSelected(match ?? data[0] ?? null)
    })
  }, [])

  return (
    <DialogShell
      title={t('businessTrip.ctx-change-country')}
      onClose={onClose}
      onConfirm={() => selected && onConfirm(selected)}
      confirmDisabled={!selected}
    >
      {/* Po `id`: `nationId` powtarza się w słowniku krajów (np. 255), więc wybór
          po nim trafiał w pierwszy kraj z tym kodem. */}
      <Dropdown
        options={countries.map((c) => ({
          label: translateCountryName(c.name, activeLanguage?.code || 'pl'),
          value: String(c.id),
        }))}
        value={selected ? String(selected.id) : null}
        onChange={(id: string) =>
          setSelected(countries.find((c) => String(c.id) === id) ?? null)
        }
        placeholder={t('businessTrip.ctx-change-country')}
        searchPlaceholder={t('common.search')}
      />
    </DialogShell>
  )
}

interface MonthPickerDialogProps {
  onClose: () => void
  onConfirm: (monthStr: string) => void
}

export const MonthPickerDialog: React.FC<MonthPickerDialogProps> = ({
  onClose,
  onConfirm,
}) => {
  const handleConfirm = (monthStr: string) => {
    onConfirm(monthStr)
    onClose()
  }

  return ReactDOM.createPortal(
    <div className="tw-fixed tw-inset-0 tw-z-[9999] tw-flex tw-items-center tw-justify-center">
      <div
        className="tw-absolute tw-inset-0 tw-bg-black/40"
        onClick={onClose}
      />
      <div className="tw-relative">
        <MonthPicker value={null} onConfirm={handleConfirm} confirmSelection />
      </div>
    </div>,
    document.body,
  )
}

interface DatePickerDialogProps {
  title: string
  onClose: () => void
  onConfirm: (dateStr: string) => void
  companyId?: string
  exchangeRateDate?: string
}

export const DatePickerDialog: React.FC<DatePickerDialogProps> = ({
  title,
  onClose,
  onConfirm,
}) => {
  const [value, setValue] = React.useState<string | null>(null)

  return (
    <DialogShell
      title={title}
      onClose={onClose}
      onConfirm={() => value && onConfirm(value)}
      confirmDisabled={!value}
    >
      <DateField value={value} setValue={setValue} openDirection="up" />
    </DialogShell>
  )
}
interface DateExchangePickerDialogProps {
  title: string
  onClose: () => void
  /** `useDefaultDate` = WPF „Data kursu z konfiguracji”: serwer liczy datę osobno dla każdej delegacji. */
  onConfirm: (dateStr: string | null, useDefaultDate: boolean) => void
  exchangeRateDate?: string
}

export const DateExchangePickerDialog: React.FC<
  DateExchangePickerDialogProps
> = ({ title, exchangeRateDate, onClose, onConfirm }) => {
  const { t } = useLocalize()
  const [value, setValue] = React.useState<string | null>(
    exchangeRateDate && moment(exchangeRateDate).isValid()
      ? moment(exchangeRateDate).format('YYYY-MM-DD')
      : null,
  )
  const [useConfigDate, setUseConfigDate] = React.useState(false)

  return (
    <DialogShell
      title={title}
      onClose={onClose}
      onConfirm={() => onConfirm(useConfigDate ? null : value, useConfigDate)}
      confirmDisabled={!useConfigDate && !value}
    >
      <DateField
        value={value}
        setValue={setValue}
        openDirection="up"
        disabled={useConfigDate}
      />
      <label className="tw-mt-3 tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-text-xs tw-text-gray-600">
        <input
          type="checkbox"
          checked={useConfigDate}
          onChange={(e) => setUseConfigDate(e.target.checked)}
          className="tw-h-3.5 tw-w-3.5 tw-accent-primary"
        />
        {t('businessTrip.ctx-use-config-date')}
      </label>
    </DialogShell>
  )
}
interface VehiclePickerDialogProps {
  companyId: string
  onClose: () => void
  onConfirm: (vehicleId: string, registrationNumber: string) => void
}

export const VehiclePickerDialog: React.FC<VehiclePickerDialogProps> = ({
  companyId,
  onClose,
  onConfirm,
}) => {
  const { t } = useLocalize()
  const { getVehicles } = useBusinessTripService()
  const [vehicles, setVehicles] = React.useState<any[]>([])
  const [selectedId, setSelectedId] = React.useState<string>('')

  React.useEffect(() => {
    // WPF TripVehicleDialog: VehicleStorage.LoadAll() - tylko aktywne pojazdy.
    getVehicles({ companyId }).then((data) => {
      const active = data
        .filter((v) => v.isActive)
        .sort((a, b) =>
          String(a.registrationNumber ?? '').localeCompare(
            String(b.registrationNumber ?? ''),
          ),
        )
      setVehicles(active)
      if (active.length > 0) setSelectedId(String(active[0].id))
    })
  }, [])

  const selectedVehicle = vehicles.find((v) => String(v.id) === selectedId)

  return (
    <DialogShell
      title={t('businessTrip.ctx-set-vehicle')}
      onClose={onClose}
      onConfirm={() =>
        selectedVehicle &&
        onConfirm(
          String(selectedVehicle.id),
          selectedVehicle.registrationNumber || '',
        )
      }
      confirmDisabled={!selectedVehicle}
    >
      <Dropdown
        options={vehicles.map((v) => ({
          label: String(v.registrationNumber || v.name || v.id),
          value: String(v.id),
        }))}
        value={selectedId || null}
        onChange={(id: string) => setSelectedId(id)}
        placeholder={t('businessTrip.ctx-set-vehicle')}
        searchPlaceholder={t('vehicleSearcher.search')}
      />
    </DialogShell>
  )
}

interface BusinessTripContextMenuDialogsProps {
  dialogState: DialogState
  companyId: string
  onClose: () => void
  exchangeRateDate: string
  onConfirmCountry: (country: CountryModel) => void
  onConfirmSettlementMonth: (month: string) => void
  onConfirmSettlementDate: (date: string) => void
  onConfirmExchangeRateDate: (date: string | null, useDefaultDate: boolean) => void
  onConfirmVehicle: (vehicleId: string, registrationNumber: string) => void
}

const BusinessTripContextMenuDialogs: React.FC<
  BusinessTripContextMenuDialogsProps
> = ({
  dialogState,
  companyId,
  exchangeRateDate,
  onClose,
  onConfirmCountry,
  onConfirmSettlementMonth,
  onConfirmSettlementDate,
  onConfirmExchangeRateDate,
  onConfirmVehicle,
}) => {
  const { t } = useLocalize()
  const { type } = dialogState

  if (!type) return null

  if (type === 'changeCountry') {
    return (
      <CountryPickerDialog
        companyId={companyId}
        currentCountry={dialogState.trip?.destinationCountry}
        onClose={onClose}
        onConfirm={onConfirmCountry}
      />
    )
  }

  if (type === 'changeSettlementMonth') {
    return (
      <MonthPickerDialog
        onClose={onClose}
        onConfirm={onConfirmSettlementMonth}
      />
    )
  }

  if (type === 'changeSettlementDate') {
    return (
      <DatePickerDialog
        title={t('businessTrip.ctx-change-settlement-date')}
        onClose={onClose}
        onConfirm={onConfirmSettlementDate}
      />
    )
  }

  if (type === 'changeExchangeRateDate') {
    return (
      <DateExchangePickerDialog
        exchangeRateDate={exchangeRateDate}
        title={t('businessTrip.ctx-change-exchange-rate-date')}
        onClose={onClose}
        onConfirm={onConfirmExchangeRateDate}
      />
    )
  }

  if (type === 'setVehicle') {
    return (
      <VehiclePickerDialog
        companyId={companyId}
        onClose={onClose}
        onConfirm={onConfirmVehicle}
      />
    )
  }

  return null
}

export default BusinessTripContextMenuDialogs
